import { useMemo } from 'react';
import { I } from '../components/icons.jsx';
import { MONITOR_CRUMB } from './home-heading.js';
import {
  useMetrics, useHealth, useSqlRows,
  useNodes, useClusterMetrics, useClusterSqlRows,
  useThroughputHistory, useS3Status,
  deriveRates, isHlc, hlcToMs, hlcDeltaToMs, fmtHlcRelative, fmtHlcDuration,
} from '../data/use-metrics.js';

// ───────────────────────── helpers ─────────────────────────

// Strip the port number off a "host:port" address for display. Returns
// null when the address has no port suffix.
function portOf(addr) {
  if (typeof addr !== 'string') return null;
  const i = addr.lastIndexOf(':');
  if (i < 0) return null;
  const n = Number(addr.slice(i + 1));
  return Number.isFinite(n) ? n : null;
}

function fmtBytes(b) {
  if (b == null || !Number.isFinite(b)) return '—';
  if (b >= 1024 ** 4) return (b / 1024 ** 4).toFixed(1) + ' TB';
  if (b >= 1024 ** 3) return (b / 1024 ** 3).toFixed(1) + ' GB';
  if (b >= 1024 ** 2) return (b / 1024 ** 2).toFixed(1) + ' MB';
  if (b >= 1024)      return (b / 1024).toFixed(1) + ' KB';
  return Math.round(b) + ' B';
}

function fmtCount(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  const v = Math.round(n);
  if (v >= 1_000_000_000) return (v / 1_000_000_000).toFixed(1) + 'B';
  if (v >= 1_000_000)     return (v / 1_000_000).toFixed(1) + 'M';
  if (v >= 1_000)         return (v / 1_000).toFixed(1) + 'k';
  return v.toLocaleString();
}

function fmtMs(ns) {
  if (ns == null || !Number.isFinite(ns)) return '—';
  const ms = ns / 1_000_000;
  if (ms >= 100) return ms.toFixed(0) + ' ms';
  if (ms >= 1)   return ms.toFixed(1) + ' ms';
  return (ns / 1000).toFixed(0) + ' µs';
}

// ───────────────────────── primitives ─────────────────────────

function Spark({ data, color = '#00E5C7', height = 36 }) {
  if (!data?.length || data.length < 2) {
    return <svg viewBox="0 0 200 36" preserveAspectRatio="none" className="spark" />;
  }
  const w = 200, h = height, pad = 2;
  const max = Math.max(...data, 1), min = Math.min(...data, 0);
  const dx = (w - pad * 2) / (data.length - 1);
  const pts = data.map((v, i) => `${pad + i * dx},${pad + (h - pad * 2) * (1 - (v - min) / (max - min || 1))}`).join(' ');
  const area = `M ${pts.split(' ')[0]} L ${pts} L ${w - pad},${h - pad} L ${pad},${h - pad} Z`;
  const gid = `g${color.replace('#', '')}`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="spark">
      <defs>
        <linearGradient id={gid} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%"   stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gid})`} stroke="none" />
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" />
    </svg>
  );
}

function ThroughputChart({ history }) {
  const w = 1000, h = 200, pad = 20;
  if (!history?.length || history.length < 2) {
    return (
      <svg viewBox={`0 0 ${w} ${h}`} className="chart-svg" preserveAspectRatio="none">
        <text x={w / 2} y={h / 2} fill="#555" textAnchor="middle" fontSize="12" fontFamily="ui-monospace,monospace">collecting samples…</text>
      </svg>
    );
  }
  const max = Math.max(...history.flatMap(d => [d.reads, d.writes]), 1);
  const dx = (w - pad * 2) / Math.max(1, history.length - 1);
  const lineFor = key => history.map((d, i) => `${pad + i * dx},${pad + (h - pad * 2) * (1 - d[key] / max)}`).join(' ');
  const areaFor = key => {
    const pts = lineFor(key);
    return `M ${pad},${h - pad} L ${pts} L ${w - pad},${h - pad} Z`;
  };
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="chart-svg" preserveAspectRatio="none">
      <defs>
        <linearGradient id="readsG" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%"   stopColor="#00E5C7" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#00E5C7" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="writesG" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%"   stopColor="#0099FF" stopOpacity="0.30" />
          <stop offset="100%" stopColor="#0099FF" stopOpacity="0" />
        </linearGradient>
      </defs>
      {[0.25, 0.5, 0.75].map(g => (
        <line key={g} x1={pad} x2={w - pad} y1={pad + (h - pad * 2) * g} y2={pad + (h - pad * 2) * g} stroke="rgba(255,255,255,0.04)" strokeWidth="1" />
      ))}
      <path d={areaFor('reads')}  fill="url(#readsG)" />
      <path d={areaFor('writes')} fill="url(#writesG)" />
      <polyline points={lineFor('reads')}  fill="none" stroke="#00E5C7" strokeWidth="1.5" />
      <polyline points={lineFor('writes')} fill="none" stroke="#0099FF" strokeWidth="1.5" />
    </svg>
  );
}

// ───────────────────────── per-node grid ─────────────────────────

function PerNodeCard({ node, metrics, prevMetrics, sampledAt, prevSampledAt, raftRole }) {
  const offline = node.status !== 'alive';
  // Authoritative role comes from SHOW RAFTGROUP 0 ON ALL NODES (`raftRole`).
  // Fall back to the metrics heuristic only while raft data is still in
  // flight on the first poll.
  const isLeader = raftRole === 'leader'
    || (raftRole == null && (metrics?.['shard_0.ops_pushed'] ?? 0) > 0);
  const role = offline ? null : (raftRole ?? (isLeader ? 'leader' : metrics ? 'follower' : '…'));
  const rates = deriveRates(metrics, prevMetrics, sampledAt, prevSampledAt);
  const queriesPerSec = rates ? rates.readsPerSec + rates.writesPerSec : null;
  const rss = node.rss_bytes ?? node.memory_total_bytes;
  const slowTotal = metrics?.['query.slow_total'];
  const wsCount = metrics?.['server.ws_active_connections'];
  const fwd = metrics?.['calvin_dml.window_forward_n'];
  const fwdNs = metrics?.['calvin_dml.window_forward_ns'];
  const avgFwdMs = fwd > 0 ? (fwdNs / fwd / 1_000_000) : null;
  const port = portOf(node.node);

  return (
    <div className="k-card" style={{ overflow: 'hidden' }}>
      <div className="k-card-head">
        <span className="title" style={{ fontFamily: 'var(--app-mono)' }}>{port != null ? `:${port}` : node.node}</span>
        <span className={'pill ' + (offline ? 'orange' : isLeader ? 'cyan' : 'green')} style={{ marginLeft: 'auto' }}>
          <span className={'kdot' + (offline ? ' orange' : '')} />
          {offline ? (node.status || 'offline') : role}
        </span>
      </div>
      <div className="k-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 12 }}>
        <Row label="status">
          <span style={{ color: !offline ? 'var(--green)' : 'var(--orange)' }} title={node.status}>
            {offline ? 'unreachable' : 'ok'}
          </span>
        </Row>
        <Row label="queries/sec">
          <Mono>{queriesPerSec != null ? fmtCount(queriesPerSec) : '—'}</Mono>
        </Row>
        <Row label="RSS">
          <Mono>{fmtBytes(rss)}</Mono>
        </Row>
        <Row label="ws connections">
          <Mono>{wsCount ?? '—'}</Mono>
        </Row>
        <Row label="slow queries">
          <Mono color={slowTotal > 0 ? 'var(--orange)' : 'var(--app-fg1)'}>{slowTotal != null ? fmtCount(slowTotal) : '—'}</Mono>
        </Row>
        {!isLeader && !offline && (
          <Row label="avg forward">
            <Mono>{avgFwdMs != null ? avgFwdMs.toFixed(1) + ' ms' : '—'}</Mono>
          </Row>
        )}
      </div>
    </div>
  );
}

function PerNodeGrid() {
  // Cluster-wide hooks: SHOW NODES + SHOW METRICS ON ALL NODES + SHOW
  // RAFTGROUP 0 ON ALL NODES. All fan out from the connected node, so
  // the browser only ever talks to a single endpoint. The grid renders
  // one card per peer in SHOW NODES.
  const { nodes } = useNodes(5000);
  const { byNode, prevByNode, sampledAt, prevSampledAt } = useClusterMetrics(1500);
  const raft = useClusterSqlRows('SHOW RAFTGROUP 0 ON ALL NODES', 3000);
  const roleByNode = useMemo(() => {
    const m = {};
    const cols = raft.columns ?? [];
    const roleIdx = cols.indexOf('role');
    if (roleIdx < 0 || !raft.byNode) return m;
    for (const [addr, rows] of Object.entries(raft.byNode)) {
      if (rows.length > 0) m[addr] = rows[0][roleIdx];
    }
    return m;
  }, [raft.byNode, raft.columns]);
  const list = nodes ?? [];
  const cols = list.length === 0 ? 1 : Math.min(list.length, 3);
  return (
    <div className="k-card full" style={{ background: 'transparent', border: 0, padding: 0 }}>
      <div className="k-card-head" style={{ background: 'transparent', border: 0, paddingLeft: 0 }}>
        <span className="title">Per-node</span>
      </div>
      {list.length === 0 ? (
        <div className="topo-empty">No nodes reported yet.</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 12 }}>
          {list.map(n => (
            <PerNodeCard
              key={n.node}
              node={n}
              metrics={byNode?.[n.node]}
              prevMetrics={prevByNode?.[n.node]}
              sampledAt={sampledAt}
              prevSampledAt={prevSampledAt}
              raftRole={roleByNode[n.node]}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ───────────────────────── memory reporters ─────────────────────────

function MemoryReporters({ rss }) {
  // SHOW MEMORY returns rows [name, bytes, entries, can_shed] sorted desc.
  // Per the SQL-over-REST rule, prefer this to /health.memory[] for diagnostics.
  const { columns, rows: raw, error } = useSqlRows('SHOW MEMORY', '', 5000);
  const colIdx = name => (columns ?? []).indexOf(name);
  const rows = raw ?? [];
  const reporterTotal = useMemo(() => {
    const bi = colIdx('bytes');
    return rows.reduce((s, r) => s + (Number(r[bi]) || 0), 0);
  }, [rows, columns]);
  return (
    <div className="k-card">
      <div className="k-card-head">
        <span className="title">Memory reporters · leader</span>
        {error && <span className="pill orange" style={{ marginLeft: 'auto' }}>error</span>}
        {!error && (
          <span className="pill" style={{ marginLeft: 'auto', fontFamily: 'var(--app-mono)' }}>
            {fmtBytes(reporterTotal)}{rss ? ` / ${fmtBytes(rss)} RSS` : ''}
          </span>
        )}
      </div>
      <table className="k-table">
        <thead>
          <tr>
            <th>component</th>
            <th className="num">bytes</th>
            <th className="num">entries</th>
            <th>shed</th>
          </tr>
        </thead>
        <tbody>
          {!raw ? (
            <tr><td colSpan={4} style={{ padding: 16, color: 'var(--app-fg3)' }}>loading…</td></tr>
          ) : rows.length === 0 ? (
            <tr><td colSpan={4} style={{ padding: 16, color: 'var(--app-fg3)' }}>no reporters</td></tr>
          ) : rows.map((r, i) => (
            <tr key={r[colIdx('name')] ?? i}>
              <td className="fg1" style={{ fontFamily: 'var(--app-mono)', fontSize: 12 }}>{r[colIdx('name')]}</td>
              <td className="num">{fmtBytes(r[colIdx('bytes')])}</td>
              <td className="num">{(Number(r[colIdx('entries')]) ?? 0).toLocaleString()}</td>
              <td>{r[colIdx('can_shed')] ? <span className="pill green" style={{ fontSize: 10 }}>yes</span> : <span style={{ color: 'var(--app-fg4)' }}>—</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ───────────────────────── Calvin pipeline phases ─────────────────────────

function CalvinPhases({ metrics }) {
  const phases = [
    { key: 'translate', label: 'translate', color: '#00E5C7', val: metrics?.['calvin_dml.window_translate_ns'] || 0 },
    { key: 'propose',   label: 'propose',   color: '#0099FF', val: metrics?.['calvin_dml.window_propose_ns']   || 0 },
    { key: 'wait',      label: 'wait',      color: '#FF9800', val: metrics?.['calvin_dml.window_wait_ns']      || 0 },
    { key: 'post',      label: 'post',      color: '#7B68EE', val: metrics?.['calvin_dml.window_post_ns']      || 0 },
    { key: 'forward',   label: 'forward',   color: '#8890A0', val: metrics?.['calvin_dml.window_forward_ns']   || 0 },
  ];
  const total = phases.reduce((s, p) => s + p.val, 0);
  const calls = metrics?.['calvin_dml.calls_total'] || 0;
  const avgUs = (calls > 0) ? (total / calls / 1000) : null;

  return (
    <div className="k-card">
      <div className="k-card-head">
        <span className="title">Calvin pipeline · where DML time goes</span>
        <span className="pill" style={{ marginLeft: 'auto', fontFamily: 'var(--app-mono)' }}>
          {avgUs != null ? avgUs.toFixed(0) + ' µs avg' : '—'}
        </span>
      </div>
      <div className="k-card-body">
        {total === 0 ? (
          <div style={{ color: 'var(--app-fg3)', fontSize: 13, padding: '4px 0' }}>no DML traffic on this node</div>
        ) : (
          <>
            <div style={{ display: 'flex', height: 20, borderRadius: 4, overflow: 'hidden', marginBottom: 12, background: 'var(--app-surface-2)' }}>
              {phases.map(p => p.val > 0 && (
                <div key={p.key} style={{ width: `${(p.val / total) * 100}%`, background: p.color }} title={`${p.label}: ${(p.val / total * 100).toFixed(1)}%`} />
              ))}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12 }}>
              {phases.filter(p => p.val > 0).map(p => (
                <div key={p.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: p.color, flexShrink: 0 }} />
                  <span style={{ color: 'var(--app-fg2)', minWidth: 80 }}>{p.label}</span>
                  <span style={{ flex: 1, color: 'var(--app-fg3)', fontFamily: 'var(--app-mono)' }}>{fmtMs(p.val)}</span>
                  <span style={{ color: 'var(--app-fg1)', fontFamily: 'var(--app-mono)', fontWeight: 600 }}>{(p.val / total * 100).toFixed(1)}%</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ───────────────────────── S3 archive (SHOW S3 STATUS) ─────────────────────────

function S3Panel() {
  // The store-backed source returns parsed scalars (lag/synced/local/pending)
  // and a 60-deep ring buffer of {t, lag, synced} samples. The ring buffer
  // survives unmount, so coming back to /monitor sees a populated trend.
  const { isDisabled, local, synced, lag, pending, history, error } = useS3Status(2000);
  // "Behind real-time by" — how far back the most-recent synced txn is from
  // wall-clock now. Only meaningful for HLC values.
  const behindMs = isHlc(synced) ? Math.max(0, Date.now() - hlcToMs(synced)) : null;
  const lagIsHlc = isHlc(local);  // if `local` is HLC, lag delta is too
  const lagMs = lagIsHlc ? hlcDeltaToMs(lag) : null;

  return (
    <div className="k-card">
      <div className="k-card-head">
        <span className="title">S3 archive · leader</span>
        {error
          ? <span className="pill orange" style={{ marginLeft: 'auto' }}>error</span>
          : isDisabled
            ? <span className="pill" style={{ marginLeft: 'auto', color: 'var(--app-fg3)' }}>disabled here</span>
            : <span className={'pill ' + (lag === 0 ? 'green' : lag > 1000 ? 'orange' : 'cyan')} style={{ marginLeft: 'auto' }}>
                {lag === 0 ? 'caught up' : lag > 1000 ? 'falling behind' : 'syncing'}
              </span>}
      </div>
      <div className="k-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 12 }}>
        {isDisabled ? (
          <div style={{ color: 'var(--app-fg3)' }}>S3 archival isn't enabled on this node — open the page from the leader.</div>
        ) : (
          <>
            <Row label="latest local txn">
              <Mono>{isHlc(local) ? fmtHlcRelative(local) : (local != null ? local.toLocaleString() : '—')}</Mono>
            </Row>
            <Row label="latest synced">
              <Mono>{isHlc(synced) ? fmtHlcRelative(synced) : (synced != null ? synced.toLocaleString() : '—')}</Mono>
            </Row>
            <Row label="lag">
              <Mono color={lag === 0 ? 'var(--green)' : (lagIsHlc ? lagMs > 1000 : lag > 1000) ? 'var(--orange)' : 'var(--app-fg1)'}>
                {lagIsHlc ? fmtHlcDuration(lag) : (lag != null ? lag.toLocaleString() + ' txn' : '—')}
              </Mono>
            </Row>
            <Row label="pending segments">
              <Mono>{pending ?? '—'}</Mono>
            </Row>
            {behindMs != null && (
              <Row label="behind real-time">
                <Mono color={behindMs > 5000 ? 'var(--orange)' : 'var(--app-fg1)'}>{behindMs < 1000 ? `${behindMs} ms` : `${(behindMs / 1000).toFixed(1)} s`}</Mono>
              </Row>
            )}
            {history.length >= 2 && (
              <div style={{ marginTop: 4 }}>
                <div style={{ fontSize: 10, color: 'var(--app-fg3)', marginBottom: 4 }}>lag trend (last {history.length * 2}s)</div>
                <Spark
                  data={history.map(h => lagIsHlc ? hlcDeltaToMs(h.lag) : h.lag)}
                  color={(lagIsHlc ? lagMs : lag) > 1000 ? '#FF9800' : '#7B68EE'}
                  height={28}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ───────────────────────── replication (SHOW PEERS) ─────────────────────────

function ReplicationPanel() {
  // SHOW PEERS ON ALL NODES returns one row per (responding node × peer).
  // We pick the rows whose responding `node` is the cluster leader, since
  // only that node's view has populated match_index / lag_entries. Going
  // through ON ALL NODES means a follower-connected client still sees the
  // leader's view — no per-node addressing required.
  const peers = useClusterSqlRows('SHOW PEERS ON ALL NODES', 2000);
  const colIdx = name => (peers.columns ?? []).indexOf(name);
  const matchIdx = colIdx('match_index');
  const roleIdx = colIdx('role');
  const error = peers.error;

  // Find the leader's address by looking for the responding node whose
  // own SHOW PEERS rows have populated match_index for at least one peer.
  // Falls back to scanning the role column for `leader`.
  const leaderAddr = useMemo(() => {
    if (!peers.byNode) return null;
    for (const [addr, rows] of Object.entries(peers.byNode)) {
      if (rows.some(r => r[matchIdx] != null)) return addr;
    }
    for (const [addr, rows] of Object.entries(peers.byNode)) {
      if (rows.some(r => r[roleIdx] === 'leader')) return addr;
    }
    return null;
  }, [peers.byNode, matchIdx, roleIdx]);

  // Leader's per-peer rows. These are what the table renders.
  const leaderRows = leaderAddr ? (peers.byNode[leaderAddr] ?? []) : [];
  // The leader's own self-row has null lag fields; only the follower
  // rows are useful in the table.
  const liveRows = leaderRows.filter(r => r[matchIdx] != null);
  const isSoloCluster = !!leaderAddr && leaderRows.length === 1;

  const stateColor = s => s === 'up' ? 'var(--green)' : s === 'lagging' ? 'var(--orange)' : 'var(--red, #FF5252)';

  return (
    <div className="k-card">
      <div className="k-card-head">
        <span className="title">Replication · per-follower</span>
        {error && <span className="pill orange" style={{ marginLeft: 'auto' }}>error</span>}
      </div>
      {!peers.rows ? (
        <div style={{ padding: 16, color: 'var(--app-fg3)', fontSize: 13 }}>loading…</div>
      ) : isSoloCluster ? (
        <div style={{ padding: 16, color: 'var(--app-fg3)', fontSize: 13 }}>
          single-node cluster — no followers to replicate to. Add a peer with{' '}
          <span className="mono" style={{ color: 'var(--cyan)' }}>--join</span> to populate this view.
        </div>
      ) : !leaderAddr || liveRows.length === 0 ? (
        <div style={{ padding: 16, color: 'var(--app-fg3)', fontSize: 13 }}>
          waiting for the leader's view to arrive…
        </div>
      ) : (
        <table className="k-table">
          <thead>
            <tr>
              <th>peer</th>
              <th>role</th>
              <th className="num">match</th>
              <th className="num">next</th>
              <th className="num">lag</th>
              <th className="num">last AE</th>
              <th>state</th>
            </tr>
          </thead>
          <tbody>
            {liveRows.map((r, i) => {
              const peer = r[colIdx('peer')];
              const role = r[colIdx('role')];
              const next = r[colIdx('next_index')];
              const match = r[matchIdx];
              const lastAe = r[colIdx('last_ae_ms_ago')];
              const lag = r[colIdx('lag_entries')];
              const state = r[colIdx('state')];
              return (
                <tr key={peer ?? i}>
                  <td className="fg1" style={{ fontFamily: 'var(--app-mono)', fontSize: 12 }}>{peer}</td>
                  <td>{role}</td>
                  <td className="num">{match?.toLocaleString() ?? '—'}</td>
                  <td className="num">{next?.toLocaleString() ?? '—'}</td>
                  <td className="num" style={{ color: lag > 0 ? 'var(--orange)' : 'var(--app-fg1)' }}>{lag ?? '—'}</td>
                  <td className="num">{lastAe != null ? `${lastAe} ms` : '—'}</td>
                  <td><span className="pill" style={{ background: 'transparent', border: 0, color: stateColor(state), padding: 0 }}>● {state}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ───────────────────────── slow queries (SHOW SLOW QUERIES) ─────────────────────────

function SlowQueriesPanel() {
  const { columns, rows, error } = useSqlRows('SHOW SLOW QUERIES', '', 5000);
  const colIdx = name => (columns ?? []).indexOf(name);
  // Newest first.
  const sorted = useMemo(() => {
    if (!rows) return null;
    const tsCol = colIdx('ts_ms');
    return rows.slice().sort((a, b) => (b[tsCol] || 0) - (a[tsCol] || 0));
  }, [rows, columns]);
  const showRows = (sorted ?? []).slice(0, 50);

  return (
    <div className="k-card full">
      <div className="k-card-head">
        <span className="title">Slow queries · last {sorted?.length ?? '…'} (showing {showRows.length})</span>
        {error && <span className="pill orange" style={{ marginLeft: 'auto' }}>error: {error}</span>}
      </div>
      {!rows ? (
        <div style={{ padding: 16, color: 'var(--app-fg3)', fontSize: 13 }}>loading…</div>
      ) : showRows.length === 0 ? (
        <div style={{ padding: 16, color: 'var(--app-fg3)', fontSize: 13 }}>no slow queries logged yet.</div>
      ) : (
        <table className="k-table">
          <thead>
            <tr>
              <th style={{ width: 80 }}>when</th>
              <th className="num">duration</th>
              <th className="num">rows</th>
              <th>error</th>
              <th>sql</th>
            </tr>
          </thead>
          <tbody>
            {showRows.map((r, i) => {
              const ts = r[colIdx('ts_ms')];
              const dur = r[colIdx('duration_us')];
              const nrows = r[colIdx('rows')];
              const err = r[colIdx('error')];
              const sql = r[colIdx('sql')];
              const ago = ts ? Math.floor((Date.now() - ts) / 1000) : null;
              return (
                <tr key={i}>
                  <td style={{ fontFamily: 'var(--app-mono)', fontSize: 11, color: 'var(--app-fg3)' }}>{ago != null ? (ago < 60 ? `${ago}s ago` : ago < 3600 ? `${Math.floor(ago / 60)}m ago` : `${Math.floor(ago / 3600)}h ago`) : '—'}</td>
                  <td className="num" style={{ color: dur > 100_000 ? 'var(--orange)' : 'var(--app-fg1)' }}>{dur != null ? fmtMs(dur * 1000) : '—'}</td>
                  <td className="num">{nrows != null && nrows >= 0 ? nrows : '—'}</td>
                  <td style={{ color: 'var(--orange)', fontSize: 12, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={err || ''}>{err || ''}</td>
                  <td className="fg1" style={{ fontFamily: 'var(--app-mono)', fontSize: 12, maxWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={sql}>{sql}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ───────────────────────── helpers (small) ─────────────────────────

function Row({ label, children }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
      <span style={{ color: 'var(--app-fg3)' }}>{label}</span>
      {children}
    </div>
  );
}

function Mono({ children, color = 'var(--app-fg1)' }) {
  return <span style={{ color, fontFamily: 'var(--app-mono)', fontWeight: 600 }}>{children}</span>;
}

// ───────────────────────── page ─────────────────────────

export function MonitorPage() {
  // Cluster-aggregate signals come from the connected node — same-origin,
  // works behind any single-endpoint reverse proxy.
  const { latest: metrics, prev, sampledAt, prevSampledAt, error: metricsErr } = useMetrics('', 1500);
  const { health, error: healthErr } = useHealth('', 5000);
  const rates = deriveRates(metrics, prev, sampledAt, prevSampledAt) ?? { readsPerSec: 0, writesPerSec: 0 };
  const history = useThroughputHistory(1500);

  const slowTotal       = metrics?.['query.slow_total'];
  const slowThresholdMs = metrics?.['query.slow_threshold_us'] != null ? Math.round(metrics['query.slow_threshold_us'] / 1000) : null;
  const totalMem        = health?.rss_bytes ?? health?.memory_total_bytes;
  const peers           = health?.peers ?? [];

  const status = (() => {
    if (healthErr) return { label: 'unreachable', color: 'var(--orange)' };
    if (!health)   return { label: '…',           color: 'var(--app-fg3)' };
    return /^ok\b/i.test(health.status)
      ? { label: 'healthy',  color: 'var(--green)'  }
      : { label: 'degraded', color: 'var(--orange)' };
  })();

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <div className="crumbs">{MONITOR_CRUMB}</div>
          <h1>Monitor</h1>
        </div>
        <div className="actions">
          <span className="pill" style={{ background: 'rgba(0,229,199,0.10)', color: status.color, border: '1px solid rgba(0,229,199,0.25)' }}>
            <span className="kdot" style={{ background: status.color }} /> {status.label}
            {peers.length > 0 && <span style={{ marginLeft: 6, color: 'var(--app-fg3)' }}>· {peers.length} nodes</span>}
          </span>
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
        {/* KPIs */}
        <div className="kpis" style={{ marginBottom: 16 }}>
          <div className="kpi-card">
            <div className="label">reads / sec</div>
            <div className="value" style={{ color: 'var(--cyan)' }}>{fmtCount(rates.readsPerSec)}</div>
            <div className="delta">leader-side, derived</div>
            <Spark data={history.map(h => h.reads)} color="#00E5C7" />
          </div>
          <div className="kpi-card">
            <div className="label">writes / sec</div>
            <div className="value" style={{ color: 'var(--blue)' }}>{fmtCount(rates.writesPerSec)}</div>
            <div className="delta">routed via Calvin</div>
            <Spark data={history.map(h => h.writes)} color="#0099FF" />
          </div>
          <div className="kpi-card">
            <div className="label">slow queries{slowThresholdMs != null ? ` (>${slowThresholdMs} ms)` : ''}</div>
            <div className="value" style={{ color: slowTotal > 0 ? 'var(--orange)' : 'var(--app-fg1)' }}>{fmtCount(slowTotal)}</div>
            <div className="delta">cumulative · log coming</div>
          </div>
          <div className="kpi-card">
            <div className="label">memory · leader</div>
            <div className="value">{fmtBytes(totalMem)}</div>
            <div className="delta">{health?.memory?.length != null ? `${health.memory.length} reporters` : '…'}</div>
          </div>
        </div>

        {/* Throughput chart full width */}
        <div className="k-card full" style={{ marginBottom: 16 }}>
          <div className="k-card-head">
            <span className="title">throughput · last {Math.round(history.length * 1.5)}s</span>
            <span style={{ display: 'flex', gap: 16, fontSize: 11, color: 'var(--app-fg3)', textTransform: 'none', letterSpacing: 0 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ width: 8, height: 2, background: '#00E5C7', borderRadius: 1 }} /> reads</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ width: 8, height: 2, background: '#0099FF', borderRadius: 1 }} /> writes</span>
            </span>
            {metricsErr && <span className="pill orange" style={{ marginLeft: 8 }}>error: {metricsErr}</span>}
          </div>
          <div style={{ padding: '8px 16px 16px' }}>
            <ThroughputChart history={history} />
          </div>
        </div>

        {/* Per-node grid */}
        <div style={{ marginBottom: 16 }}>
          <PerNodeGrid />
        </div>

        {/* Row 1: Replication | S3   Row 2: Memory | Calvin   Row 3: Slow queries (full) */}
        <div className="mon-grid">
          <ReplicationPanel />
          <S3Panel />
          <MemoryReporters rss={health?.rss_bytes ?? health?.memory_total_bytes} />
          <CalvinPhases metrics={metrics} />
          <SlowQueriesPanel />
        </div>
      </div>
    </div>
  );
}
